import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dropdown,
    Input,
    InputOnChangeData,
    Label,
    Link,
    Option,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    ToolbarButton,
} from '@fluentui/react-components';
import { Dismiss24Regular, Filter24Regular, Search24Regular } from '@fluentui/react-icons';
import {
    ConnectionStatusFilter,
    ConnectionStatusFilterValues,
    CustomerNotificationStatus,
    DepartureBoardFilterState,
    FlightCheckedInStatus,
    FlightHaulType,
} from '../../../store/features/departureBoard/departureBoardSlice';
import { FlightDepartureStatus } from '../../../model';
import { CustomerNotificationFilter } from '../components/FilterToolbar';
import { useDepartureBoardPageStyles } from '../DepartureBoardPage.styles';
import { useToolbarStyles } from '../../../controls';
import { getAppliedFiltersCount } from '../utils/departureBoardFiltersUtils';
import { getHaulTypeLabels } from '../utils/departureBoardFiltersUtils';

type DepartureBoardPageStyles = ReturnType<typeof useDepartureBoardPageStyles>;
type ToolbarStyles = ReturnType<typeof useToolbarStyles>;

interface DepartureBoardFiltersPopoverProps {
    open: boolean;
    filterState: DepartureBoardFilterState;
    styles: DepartureBoardPageStyles;
    toolbarStyles: ToolbarStyles;
    onToggleOpen: () => void;
    onClearFiltersClick: () => void;
    onDepartureStatusFilterChange: (status: FlightDepartureStatus, checked: boolean) => void;
    onDepartureAirportFilterChange: (airport: string, checked: boolean) => void;
    onArrivalAirportFilterChange: (airport: string, checked: boolean) => void;
    onCheckInStatusChange: (status: FlightCheckedInStatus) => void;
    onDelayedBaggageStatusChange: (status: CustomerNotificationStatus) => void;
    onMisconnectedCustomersStatusChange: (status: CustomerNotificationStatus) => void;
    onConnectionStatusChange: (status: ConnectionStatusFilter) => void;
    onHaulTypeChange: (haulType: FlightHaulType) => void;
    onQueryTextChange: (_: unknown, data: InputOnChangeData) => void;
    onClearSearchTextClick: () => void;
}

const DepartureBoardFiltersPopover: React.FC<DepartureBoardFiltersPopoverProps> = ({
    open,
    filterState,
    styles,
    toolbarStyles,
    onToggleOpen,
    onClearFiltersClick,
    onDepartureStatusFilterChange,
    onDepartureAirportFilterChange,
    onArrivalAirportFilterChange,
    onCheckInStatusChange,
    onDelayedBaggageStatusChange,
    onMisconnectedCustomersStatusChange,
    onConnectionStatusChange,
    onHaulTypeChange,
    onQueryTextChange,
    onClearSearchTextClick,
}) => {
    const { t } = useTranslation();
    const haulTypeLabels = getHaulTypeLabels(t);

    const selectedDepartureStatuses = useMemo(
        () => Object.keys(filterState.departureStatuses).filter((k) => filterState.departureStatuses[k]),
        [filterState.departureStatuses]
    );

    const selectedArrivalAirports = useMemo(
        () => Object.keys(filterState.arrivalAirports).filter((k) => filterState.arrivalAirports[k]),
        [filterState.arrivalAirports]
    );

    const selectedDepartureAirports = useMemo(
        () => Object.keys(filterState.departureAirports).filter((k) => filterState.departureAirports[k]),
        [filterState.departureAirports]
    );

    const appliedFiltersCount = useMemo(
        () => getAppliedFiltersCount(filterState),
        [filterState]
    );

    const buttonFilterLabel = useMemo(() => {
        let label = t('Button_Filter');
        if (appliedFiltersCount > 0) {
            label = `${label} (${appliedFiltersCount})`;
        }
        return label;
    }, [appliedFiltersCount, t]);

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Popover
                open={open}
                onOpenChange={(_, data) => {
                    if (!data.open && open) {
                        onToggleOpen();
                    }
                }}
            >
                <PopoverTrigger disableButtonEnhancement>
                    <ToolbarButton
                        aria-label={t('Button_Filter')!}
                        appearance="subtle"
                        icon={<Filter24Regular />}
                        onClick={onToggleOpen}
                    >
                        {buttonFilterLabel}
                    </ToolbarButton>
                </PopoverTrigger>
                <PopoverSurface>
                    <div className={styles.filtersContainer}>
                        <div className={styles.filtersHeader}>
                            <Link
                                appearance="subtle"
                                disabled={appliedFiltersCount === 0}
                                onClick={onClearFiltersClick}
                            >
                                {t('Button_Filter_ClearAll')}
                            </Link>
                        </div>
                        <div className={styles.filters}>
                            <div className={styles.filter}>
                                <Label>{t('TableHeader_HaulType', 'SH/LH')}</Label>
                                <Dropdown
                                    selectedOptions={
                                        filterState.haulType ? [filterState.haulType] : []
                                    }
                                    value={
                                        filterState.haulType
                                            ? haulTypeLabels[filterState.haulType]
                                            : undefined
                                    }
                                    onOptionSelect={(_, data) =>
                                        onHaulTypeChange(data.optionValue as FlightHaulType)
                                    }
                                >
                                    <Option value={FlightHaulType.Short}>
                                        {haulTypeLabels.Short}
                                    </Option>
                                    <Option value={FlightHaulType.Long}>
                                        {haulTypeLabels.Long}
                                    </Option>
                                </Dropdown>
                            </div>
                            <div className={styles.filter}>
                                <Label>{t('Filter_DepartureStatus')}</Label>
                                <Dropdown
                                    multiselect
                                    selectedOptions={selectedDepartureStatuses}
                                    value={selectedDepartureStatuses
                                        .map((status) => t(`DepartureStatus_${status}`))
                                        .join(', ')}
                                    onOptionSelect={(_, data) => {
                                        const checked = data.selectedOptions.indexOf(data.optionValue!) >= 0;
                                        onDepartureStatusFilterChange(
                                            data.optionValue as FlightDepartureStatus,
                                            checked
                                        );
                                    }}
                                >
                                    {(Object.values(FlightDepartureStatus) as FlightDepartureStatus[]).map((status) => (
                                        <Option
                                            key={`option_${status}`}
                                            text={t(`DepartureStatus_${status}`)!}
                                            value={status}
                                        >
                                            {t(`DepartureStatus_${status}`)!}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filter}>
                                <Label>{t('Filter_DepartureAirport')}</Label>
                                <Dropdown
                                    multiselect
                                    selectedOptions={selectedDepartureAirports}
                                    value={selectedDepartureAirports.join(', ')}
                                    onOptionSelect={(_, data) => {
                                        const checked = data.selectedOptions.indexOf(data.optionValue!) >= 0;
                                        onDepartureAirportFilterChange(data.optionValue!, checked);
                                    }}
                                >
                                    {Object.keys(filterState.departureAirports).map((airport) => (
                                        <Option key={`option_${airport}`} text={airport} value={airport}>
                                            {airport}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filter}>
                                <Label>{t('Filter_ArrivalAirport')}</Label>
                                <Dropdown
                                    multiselect
                                    selectedOptions={selectedArrivalAirports}
                                    value={selectedArrivalAirports.join(', ')}
                                    onOptionSelect={(_, data) => {
                                        const checked = data.selectedOptions.indexOf(data.optionValue!) >= 0;
                                        onArrivalAirportFilterChange(data.optionValue!, checked);
                                    }}
                                >
                                    {Object.keys(filterState.arrivalAirports).map((airport) => (
                                        <Option key={`option_${airport}`} text={airport} value={airport}>
                                            {airport}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className={styles.filter}>
                                <Label>{t('Filter_CheckInStatus')}</Label>
                                <Dropdown
                                    selectedOptions={
                                        filterState.flightsCheckedInStatus ? [filterState.flightsCheckedInStatus] : []
                                    }
                                    value={
                                        filterState.flightsCheckedInStatus
                                            ? t(`CheckInStatus_${filterState.flightsCheckedInStatus}`)
                                            : undefined
                                    }
                                    onOptionSelect={(_, data) => {
                                        onCheckInStatusChange(data.optionValue as FlightCheckedInStatus);
                                    }}
                                >
                                    <Option key="option_yes" text={t('CheckInStatus_Yes')!} value={FlightCheckedInStatus.Yes}>
                                        {t('CheckInStatus_Yes')}
                                    </Option>
                                    <Option key="option_no" text={t('CheckInStatus_No')!} value={FlightCheckedInStatus.No}>
                                        {t('CheckInStatus_No')}
                                    </Option>
                                </Dropdown>
                            </div>
                            <div className={styles.filter}>
                                <CustomerNotificationFilter
                                    label={t('Filter_DelayedBaggage')}
                                    value={filterState.delayedBaggageStatus}
                                    onValueChange={onDelayedBaggageStatusChange}
                                />
                            </div>
                            <div className={styles.filter}>
                                <CustomerNotificationFilter
                                    label={t('Filter_MisconnectedCustomers')}
                                    value={filterState.misconnectedCustomersStatus}
                                    onValueChange={onMisconnectedCustomersStatusChange}
                                />
                            </div>
                            <div className={styles.filter}>
                                <Label>{t('Filter_ConnectionStatus')}</Label>
                                <Dropdown
                                    selectedOptions={
                                        filterState.connectionStatus ? [filterState.connectionStatus] : []
                                    }
                                    value={
                                        filterState.connectionStatus
                                            ? t(`Filter_ConnectionStatus_${filterState.connectionStatus}`)
                                            : undefined
                                    }
                                    onOptionSelect={(_, data) => {
                                        onConnectionStatusChange(data.optionValue as ConnectionStatusFilter);
                                    }}
                                >
                                    {ConnectionStatusFilterValues.map((status) => (
                                        <Option
                                            key={`option_${status}`}
                                            text={t(`Filter_ConnectionStatus_${status}`)}
                                            value={status}
                                        >
                                            {t(`Filter_ConnectionStatus_${status}`)}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                </PopoverSurface>
            </Popover>
            <Input
                className={toolbarStyles.searchBar}
                placeholder={t('SearchFlightsInputPlaceholder')!}
                size="small"
                onChange={onQueryTextChange}
                value={filterState.queryText}
            />
            <Button
                appearance="transparent"
                onClick={onClearSearchTextClick}
                icon={!filterState.queryText ? <Search24Regular /> : <Dismiss24Regular />}
            />
        </div>
    );
};

export default DepartureBoardFiltersPopover;
